import { useCallback, useEffect, useState } from 'react';

/**
 * Reports whether a newer build has been deployed since this tab loaded.
 *
 * nginx serves index.html with `no-store`, so a fresh page load always picks
 * up the newest bundle. This covers the other case: a courier who keeps the
 * app open for hours -- routine when the link arrives from WhatsApp/ManyChat
 * and opens in an in-app webview that is never closed. Such a session keeps
 * running old code until something tells it not to.
 *
 * The running build id travels in the `app-build-id` meta tag (stamped into
 * index.html by scripts/stamp-version.js); the deployed one is polled from
 * /version.json. A difference means a deploy landed under this session.
 */

const META_NAME = 'app-build-id';
const PLACEHOLDER = '__APP_BUILD_ID__';
const VERSION_URL = '/version.json';
const DEFAULT_INTERVAL_MS = 60_000;

const readRunningBuildId = (): string | null => {
  const meta = document.querySelector<HTMLMetaElement>(`meta[name="${META_NAME}"]`);
  const value = meta?.content?.trim();
  // Unstamped under `npm run start`, where there is nothing to detect.
  if (!value || value === PLACEHOLDER) return null;
  return value;
};

const fetchDeployedBuildId = async (): Promise<string | null> => {
  try {
    const response = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    const buildId = (body as { buildId?: unknown } | null)?.buildId;
    return typeof buildId === 'string' && buildId !== '' ? buildId : null;
  } catch {
    // Offline, or the poll raced a rollout. Staying quiet is correct: the next
    // tick retries, and a courier on a flaky phone connection is never nagged.
    return null;
  }
};

export function useAppVersionCheck(intervalMs = DEFAULT_INTERVAL_MS): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const check = useCallback(async (runningBuildId: string) => {
    const deployed = await fetchDeployedBuildId();
    if (deployed && deployed !== runningBuildId) setUpdateAvailable(true);
  }, []);

  useEffect(() => {
    const runningBuildId = readRunningBuildId();
    if (!runningBuildId) return;

    let cancelled = false;
    const run = () => {
      if (!cancelled && !document.hidden) void check(runningBuildId);
    };

    run();
    const timer = window.setInterval(run, intervalMs);
    // A backgrounded tab does not get reliable timers, so re-check on return:
    // that is the moment the courier is about to interact with stale code.
    document.addEventListener('visibilitychange', run);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', run);
    };
  }, [check, intervalMs]);

  return updateAvailable;
}
